import { validateDomain, validateUrl } from './url-validation'

interface ManifestIcon {
    src: string
    type?: string
    sizes?: string
}

interface Manifest {
    icons?: ManifestIcon[]
}

interface DDGResult {
    results?: Array<{ icon_url: string }>
}

export interface FaviconFetchResult {
    errorType: 'not-found' | 'cors-error' | null
    faviconUrl: string | null
    faviconData: string | null
}

/**
 * Simple retry function with exponential backoff
 */
async function retry<T>(
    fn: (signal?: AbortSignal) => Promise<T>,
    maxRetries: number = 1, // Reduced to 1
    baseDelay: number = 1000,
): Promise<T | null> {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn()
        } catch (error: unknown) {
            if (
                attempt === maxRetries ||
                (error instanceof Error && error.name === 'AbortError')
            ) {
                console.warn(`Failed after ${attempt + 1} attempts:`, error)
                return null
            }
            const delay = baseDelay * Math.pow(2, attempt)
            await new Promise((resolve) => setTimeout(resolve, delay))
        }
    }
    return null
}

/**
 * Expanded common favicon locations including www variant
 */
async function tryExpandedCommonLocations(
    domain: string,
    signal?: AbortSignal,
): Promise<string | null> {
    const variants = [domain, `www.${domain}`]
    const paths = [
        '/favicon.ico',
        '/favicon.png',
        '/favicon.svg',
        '/apple-touch-icon.png',
        '/apple-touch-icon.svg',
        '/icon.png',
        '/icon.svg',
        '/images/favicon.ico',
        '/static/favicon.ico',
        '/assets/icon.png',
    ]

    const allUrls = variants.flatMap((v) =>
        paths.map((p) => `https://${v}${p}`),
    )

    for (const url of allUrls) {
        if (await verifyFaviconUrl(url, signal)) {
            return url
        }
    }
    return null
}

/**
 * Extract favicon from web app manifest
 */
async function extractFromManifest(
    domain: string,
    signal?: AbortSignal,
): Promise<FaviconFetchResult | null> {
    try {
        const response = await retry(
            async (sig) =>
                fetch(`https://${domain}/manifest.json`, {
                    headers: {
                        'User-Agent':
                            'Mozilla/5.0 (compatible; FaviconFetcher/1.0)',
                    },
                    signal: sig || AbortSignal.timeout(3000),
                }),
            1,
            1000,
        )

        if (!response?.ok) return null

        const manifest = (await response.json()) as Manifest
        const icons = manifest.icons || []
        for (const icon of icons) {
            if (icon.src && typeof icon.src === 'string') {
                let iconUrl = icon.src.startsWith('http')
                    ? icon.src
                    : `https://${domain}${icon.src.startsWith('/') ? '' : '/'}${icon.src}`
                // Validate URL before fetching
                if (validateUrl(iconUrl)) {
                    const data = await fetchFaviconData(iconUrl, signal)
                    if (data) {
                        return {
                            errorType: null,
                            faviconUrl: iconUrl,
                            faviconData: data,
                        }
                    }
                }
            }
        }
    } catch (error: unknown) {
        // Ignore manifest errors
    }
    return null
}

/**
 * Validate base64 image data using sharp
 */
function validateImageData(base64: string): string | null {
    try {
        const buffer = Buffer.from(base64, 'base64')
        if (buffer.length < 4) return null

        const signature = buffer.slice(0, 8) // Check first 8 bytes for most formats

        // PNG: 89 50 4E 47 0D 0A 1A 0A
        if (
            signature[0] === 0x89 &&
            signature[1] === 0x50 &&
            signature[2] === 0x4e &&
            signature[3] === 0x47
        ) {
            return base64
        }
        // ICO: 00 00 01 00
        if (
            signature[0] === 0x00 &&
            signature[1] === 0x00 &&
            signature[2] === 0x01 &&
            signature[3] === 0x00
        ) {
            return base64
        }
        // JPEG: FF D8 FF
        if (
            signature[0] === 0xff &&
            signature[1] === 0xd8 &&
            signature[2] === 0xff
        ) {
            return base64
        }
        // GIF: 47 49 46 38 (GIF8)
        if (
            signature[0] === 0x47 &&
            signature[1] === 0x49 &&
            signature[2] === 0x46 &&
            signature[3] === 0x38
        ) {
            return base64
        }
        // WebP: 52 49 46 46 ?? ?? ?? 57 45 42 50 (RIFF ... WEBP)
        if (
            signature[0] === 0x52 &&
            signature[1] === 0x49 &&
            signature[2] === 0x46 &&
            signature[3] === 0x46 &&
            signature[7] === 0x57 &&
            signature[8] === 0x45 &&
            signature[9] === 0x42 &&
            signature[10] === 0x50
        ) {
            return base64
        }
        // SVG: starts with <svg (text)
        const svgStart = buffer.toString('utf8', 0, 5)
        if (svgStart.toLowerCase().startsWith('<svg')) {
            return base64
        }

        return null
    } catch {
        return null
    }
}

/**
 * Updated fetchFaviconData with validation
 */
export async function fetchFaviconData(
    url: string,
    signal?: AbortSignal,
): Promise<string | null> {
    // Validate URL to prevent SSRF
    if (!validateUrl(url)) {
        console.warn(`Rejected potentially unsafe URL: ${url}`)
        return null
    }

    const options = {
        timeout: 3000, // Reduced
        headers: {
            'User-Agent': 'Mozilla/5.0 (compatible; FaviconFetcher/1.0)',
        },
    }

    const data = await retry(async (sig) => {
        const response = await fetch(url, {
            method: 'GET',
            headers: options.headers,
            signal: sig || AbortSignal.timeout(options.timeout),
        })

        if (
            response.ok &&
            (response.headers.get('content-type')?.startsWith('image/') ??
                false)
        ) {
            const arrayBuffer = await response.arrayBuffer()
            const base64 = Buffer.from(arrayBuffer).toString('base64')
            return base64
        }
        return null
    })

    return data ? validateImageData(data) : null
}

/**
 * Updated verifyFaviconUrl with retry and no CORS
 */
export async function verifyFaviconUrl(
    url: string,
    signal?: AbortSignal,
): Promise<boolean> {
    // Validate URL to prevent SSRF
    if (!validateUrl(url)) {
        console.warn(`Rejected potentially unsafe URL: ${url}`)
        return false
    }

    const options = {
        timeout: 3000, // Reduced
        headers: {
            'User-Agent': 'Mozilla/5.0 (compatible; FaviconFetcher/1.0)',
        },
    }

    try {
        const response = await retry(
            async (sig) =>
                fetch(url, {
                    method: 'HEAD',
                    headers: options.headers,
                    signal: sig || AbortSignal.timeout(options.timeout),
                }),
            1,
            1000,
        )

        return (
            Boolean(response?.ok) &&
            (response?.headers.get('content-type')?.startsWith('image/') ??
                false)
        )
    } catch (error: unknown) {
        return false
    }
}

/**
 * Updated extractFaviconFromHTML without CORS mode and with retry
 */
export async function extractFaviconFromHTML(
    domain: string,
    signal?: AbortSignal,
): Promise<FaviconFetchResult> {
    const options = {
        userAgent: 'Mozilla/5.0 (compatible; FaviconFetcher/1.0)',
        timeout: 3000, // Reduced
        headers: {
            'User-Agent': 'Mozilla/5.0 (compatible; FaviconFetcher/1.0)',
        },
    }

    const response = await retry(
        async (sig) =>
            fetch(`https://${domain}`, {
                method: 'GET',
                headers: options.headers,
                signal: sig || AbortSignal.timeout(options.timeout),
            }),
        1,
        1000,
    )

    if (!response?.ok) {
        return { errorType: 'not-found', faviconUrl: null, faviconData: null } // Changed from cors-error to not-found to continue fallbacks
    }

    let html: string
    try {
        html = await response.text()
    } catch (error: unknown) {
        return { errorType: 'not-found', faviconUrl: null, faviconData: null }
    }

    // Existing regex matches...
    const faviconMatches = [
        /<link[^>]*rel=["'](?:shortcut )?icon["'][^>]*href=["']([^"']+)["']/i,
        /<link[^>]*rel=["']apple-touch-icon["'][^>]*href=["']([^"']+)["']/i,
        /<link[^>]*rel=["']icon["'][^>]*type=["']image\/png["'][^>]*href=["']([^"']+)["']/i,
    ]

    for (const regex of faviconMatches) {
        const match = html.match(regex)
        if (match && match[1]) {
            let faviconUrl = match[1]

            if (faviconUrl.startsWith('/')) {
                faviconUrl = `https://${domain}${faviconUrl}`
            } else if (!faviconUrl.startsWith('http')) {
                faviconUrl = `https://${domain}/${faviconUrl}`
            }

            // Validate URL before fetching
            if (validateUrl(faviconUrl)) {
                const faviconData = await fetchFaviconData(faviconUrl, signal)
                if (faviconData) {
                    return {
                        errorType: null,
                        faviconUrl,
                        faviconData,
                    }
                }
            }
        }
    }

    return { errorType: 'not-found', faviconUrl: null, faviconData: null }
}

/**
 * Try common favicon locations - now expanded
 */
export async function tryCommonFaviconLocations(
    domain: string,
    signal?: AbortSignal,
): Promise<string | null> {
    return tryExpandedCommonLocations(domain, signal)
}

/**
 * External fallback using DuckDuckGo
 */
async function externalFallback(
    domain: string,
    signal?: AbortSignal,
): Promise<FaviconFetchResult | null> {
    if (signal?.aborted) return null
    try {
        const ddgoUrl = `https://duckduckgo.com/i.js?q=${encodeURIComponent(domain)}&o=json`
        const response = await fetch(ddgoUrl, {
            headers: {
                'User-Agent': 'Mozilla/5.0 (compatible; FaviconFetcher/1.0)',
            },
            signal: signal || AbortSignal.timeout(3000),
        })

        if (response.ok) {
            const data = (await response.json()) as DDGResult
            const iconUrl = data.results?.[0]?.icon_url
            if (iconUrl && validateUrl(iconUrl)) {
                const faviconData = await fetchFaviconData(iconUrl, signal)
                if (faviconData) {
                    return { errorType: null, faviconUrl: iconUrl, faviconData }
                }
            }
        }
    } catch (error: unknown) {
        // Ignore
    }
    return null
}

/**
 * Exhaustive favicon fetcher combining all strategies
 */
export interface FaviconExhaustiveOptions {
    signal?: AbortSignal
}

export async function fetchFaviconExhaustive(
    domain: string,
    options?: FaviconExhaustiveOptions,
): Promise<FaviconFetchResult> {
    // Validate domain to prevent SSRF
    if (!validateDomain(domain)) {
        console.warn(`Rejected potentially unsafe domain: ${domain}`)
        return { errorType: 'not-found', faviconUrl: null, faviconData: null }
    }

    const { signal } = options || {}

    const strategies = [
        () => extractFaviconFromHTML(domain, signal),
        () => extractFromManifest(domain, signal),
        () =>
            tryExpandedCommonLocations(domain, signal).then((url) =>
                url
                    ? fetchFaviconData(url, signal).then((data) => ({
                          errorType: null,
                          faviconUrl: url,
                          faviconData: data,
                      }))
                    : null,
            ),
    ]

    const results = await Promise.allSettled(strategies.map((s) => s()))
    let bestResult: FaviconFetchResult | null = null

    // Prioritize HTML > Manifest > Paths
    for (const [i, result] of results.entries()) {
        if (
            result.status === 'fulfilled' &&
            result.value &&
            result.value.faviconData
        ) {
            if (i === 0) {
                // HTML first
                bestResult = result.value
                break
            } else if (!bestResult) {
                bestResult = result.value
            }
        }
    }

    if (bestResult) {
        return bestResult
    }

    // External fallback
    const fallback = await externalFallback(domain, signal)
    if (fallback?.faviconData) {
        return fallback
    }

    return { errorType: 'not-found', faviconUrl: null, faviconData: null }
}

/**
 * Extract domain from email address
 */
export function extractDomainFromEmail(email: string): string {
    if (!email.includes('@')) {
        return ''
    }
    return email.split('@')[1].toLowerCase().trim()
}
